import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EntityRecord } from '../../api/api-entities';
import { createAttributeValidationSchema } from './validation';
import { ItemForm, ItemFormMode } from './item-form';
import { ItemRecord } from '../../api/api-items';

import { Button } from '@@/ui/primitives';
import { SaveIcon } from 'lucide-react';
import { Spinner } from '@@/ui/components';

interface ItemEditorProps {
  entity: EntityRecord;
  item?: ItemRecord;
  mode: ItemFormMode;
  isSubmitting: boolean;
  onSubmit?: (values: Record<string, unknown>) => Promise<void>;
  onCancel?: () => void;
  children?: React.ReactNode;
  className?: string;
  formId?: string;
}

export function ItemEditor({ entity, item, onSubmit, isSubmitting }: ItemEditorProps) {
  const schema = createAttributeValidationSchema(entity);
  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: Object.keys(entity.schema.fields ?? {}).reduce(
      (defaults, key) => {
        const field = entity.schema.fields[key];

        // If we have an existing item, use its values
        if (item && item.attributes && item.attributes[key] !== undefined) {
          defaults[key] = item.attributes[key];
          return defaults;
        }

        // Otherwise use field defaults
        if (field.definition.type === 'boolean') {
          defaults[key] = false;
        } else if (field.definition.type === 'picklist' && field.definition.options.length > 0) {
          defaults[key] = field.definition.multiple ? '' : field.definition.options[0];
        } else if (field.definition.type === 'prompt') {
          defaults[key] = { content: '<speak>\n\n</speak>', kind: 'SSML' };
        } else if (field.definition.type === 'connect_resource') {
          defaults[key] = null;
        } else if (field.definition.type === 'date_time') {
          defaults[key] = { timeZone: 'UTC', date: '', time: '' };
        } else {
          defaults[key] = '';
        }
        return defaults;
      },
      {} as Record<string, unknown>
    )
  });

  // Update form values when item changes
  useEffect(() => {
    if (item && item.attributes) {
      const newValues = Object.keys(entity.schema.fields ?? {}).reduce(
        (values, key) => {
          const field = entity.schema.fields[key];

          if (item.attributes[key] !== undefined) {
            values[key] = item.attributes[key];
          } else {
            // Use default values for missing fields
            if (field.definition.type === 'boolean') {
              values[key] = false;
            } else if (field.definition.type === 'picklist' && field.definition.options.length > 0) {
              values[key] = field.definition.multiple ? '' : field.definition.options[0];
            } else if (field.definition.type === 'prompt') {
              values[key] = { content: '', kind: 'SSML' };
            } else if (field.definition.type === 'connect_resource') {
              values[key] = null;
            } else if (field.definition.type === 'date_time') {
              values[key] = { timeZone: 'UTC', date: '', time: '' };
            } else if (field.definition.type === 'reference') {
              values[key] = { ref: { eid: '', iid: '' } };
            } else {
              values[key] = '';
            }
          }
          return values;
        },
        {} as Record<string, unknown>
      );

      form.reset(newValues);
    }
  }, [item, entity.schema.fields, form]);

  const handleFormSubmit = (values: FormValues) => {
    if (onSubmit) {
      onSubmit(values);
    }
  };

  return (
    <FormProvider {...form}>
      <form {...form} onSubmit={form.handleSubmit(handleFormSubmit)}>
        <ItemForm entity={entity} form={form} />
        <div className='flex justify-end gap-2 px-4 pb-6 pt-6'>
          <Button
            type='submit'
            variant='default'
            size='sm'
            disabled={isSubmitting || !form.formState.isValid}
            className='text-sm! leading-snug font-medium! px-3!'
          >
            <SaveIcon />
            Save
            {isSubmitting ? <Spinner /> : null}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
